/**
 * RulesPanel Component
 * Displays and manages extraction rules.
 * Allows users to enable/disable, edit, and delete rules.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Switch,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
  Alert,
  CircularProgress,
  Tooltip,
  Divider,
  Checkbox,
  FormControlLabel,
  InputAdornment,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Code as PatternIcon,
  Rule as RuleIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  PlayArrow as TestIcon,
  CheckCircle as MatchIcon,
  Cancel as NoMatchIcon,
  DriveFileMove as MoveIcon,
  Public as GlobalIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { ExtractionRule, RuleScope, RuleCategory, RuleAction, CustomerFolder } from '../../types/bom.types';
import * as bomApi from '../../services/bomApi';

interface RulesPanelProps {
  onRulesChange?: () => void;
  compact?: boolean;
  /** Callback to highlight a reference in the PDF */
  onHighlightReference?: (reference: string, componentType?: string) => void;
  /** Current folder context for creating folder-scoped rules */
  currentFolderId?: string;
  /** When true, disables all editing (for locked extractions) */
  readOnly?: boolean;
  /** Current job ID for applying rules to extraction */
  currentJobId?: string | null;
  /** Current PDF filename for incremental extraction */
  currentPdfFilename?: string | null;
  /** Callback when rule is applied and extraction needs refresh */
  onRuleApplied?: (result: { items_added: number; message: string }) => void;
}

const SCOPE_COLORS: Record<string, string> = {
  global: '#4caf50',
  project: '#2196f3',
  file: '#ff9800',
  folder: '#9c27b0', // Purple for folder-scoped
};

const ACTION_COLORS: Record<RuleAction, string> = {
  include: '#4caf50',
  exclude: '#f44336',
  force_include: '#ff9800', // Orange for force_include - high priority override
};

const RulesPanel: React.FC<RulesPanelProps> = ({ onRulesChange, compact = false, onHighlightReference, currentFolderId, readOnly = false, currentJobId, currentPdfFilename, onRuleApplied }) => {
  const [rules, setRules] = useState<ExtractionRule[]>([]);
  const [folders, setFolders] = useState<CustomerFolder[]>([]);
  const [componentTypes, setComponentTypes] = useState<{ name: string; display_name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedRules, setExpandedRules] = useState<Set<string>>(new Set());
  const [filterScope, setFilterScope] = useState<string>('all'); // Can be 'all', 'global', 'folder', or a specific folder ID
  const [searchTerm, setSearchTerm] = useState<string>(''); // Search filter for rules
  
  // Dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<ExtractionRule | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingRule, setDeletingRule] = useState<ExtractionRule | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  
  // New rule form
  const [patternMode, setPatternMode] = useState<'example' | 'regex'>('example');
  const blankRule = useCallback(() => ({
    name: '',
    text: '',
    pattern: '',
    component_type: '',
    scope: (currentFolderId ? 'folder' : 'global') as RuleScope,
    scope_value: currentFolderId || '',
    category: 'electrical' as RuleCategory,
    action: 'include' as RuleAction,
    description: '',
  }), [currentFolderId]);
  const [newRule, setNewRule] = useState({
    name: '',
    text: '',
    pattern: '',
    component_type: '',
    scope: 'global' as RuleScope,
    scope_value: '' as string, // folder_id when scope is 'folder'
    category: 'electrical' as RuleCategory,
    action: 'include' as RuleAction,
    description: '',
  });
  const [applyToExtraction, setApplyToExtraction] = useState(true); // Default to applying
  const [applyingRule, setApplyingRule] = useState(false);
  
  // Test rule state
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testingRule, setTestingRule] = useState<ExtractionRule | null>(null);
  const [testValues, setTestValues] = useState('');
  const [testResults, setTestResults] = useState<bomApi.RuleTestResult[]>([]);
  const [testLoading, setTestLoading] = useState(false);
  
  // Move rule to customer dialog state
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [movingRule, setMovingRule] = useState<ExtractionRule | null>(null);
  const [targetFolderId, setTargetFolderId] = useState<string>('');

  // Load folders for the scope dropdown
  const loadFolders = useCallback(async () => {
    try {
      const data = await bomApi.listFolders();
      setFolders(data);
    } catch (err: any) {
      console.error('Failed to load folders:', err);
    }
  }, []);

  // Load component types for dropdown
  const loadComponentTypes = useCallback(async () => {
    try {
      const data = await bomApi.getComponentTypes();
      setComponentTypes(data);
    } catch (err: any) {
      console.error('Failed to load component types:', err);
    }
  }, []);

  // Load rules
  const loadRules = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Determine scope filter
      let scopeFilter: string | undefined;
      let folderFilter: string | undefined;
      
      if (filterScope === 'all') {
        // No filter
      } else if (filterScope === 'global') {
        scopeFilter = 'global';
      } else if (filterScope === 'folder') {
        scopeFilter = 'folder';
      } else if (filterScope.startsWith('folder:')) {
        // Specific folder ID
        folderFilter = filterScope.replace('folder:', '');
      }
      
      const response = await bomApi.getRules(scopeFilter);
      let filteredRules = response.rules;
      
      // Apply folder filter if specified
      if (folderFilter) {
        filteredRules = filteredRules.filter(r => r.scope_value === folderFilter);
      }
      
      setRules(filteredRules);
    } catch (err: any) {
      setError(err.message || 'Failed to load rules');
    } finally {
      setLoading(false);
    }
  }, [filterScope]);

  useEffect(() => {
    loadFolders();
    loadComponentTypes();
  }, [loadFolders, loadComponentTypes]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  // Auto-select folder filter when a folder context is set (e.g., when loading a PDF assigned to a folder)
  useEffect(() => {
    if (currentFolderId) {
      console.log('📁 RulesPanel: Auto-selecting folder filter:', currentFolderId);
      setFilterScope(`folder:${currentFolderId}`);
    }
  }, [currentFolderId]);

  // Toggle rule enabled/disabled
  const handleToggleRule = async (rule: ExtractionRule) => {
    try {
      await bomApi.toggleRule(rule.id, !rule.enabled);
      setRules(rules.map(r => 
        r.id === rule.id ? { ...r, enabled: !r.enabled } : r
      ));
      onRulesChange?.();
    } catch (err: any) {
      setError(`Failed to toggle rule: ${err.message}`);
    }
  };

  // Delete rule
  const handleDeleteRule = async () => {
    if (!deletingRule) return;
    try {
      await bomApi.deleteRule(deletingRule.id);
      setRules(rules.filter(r => r.id !== deletingRule.id));
      setDeleteDialogOpen(false);
      setDeletingRule(null);
      onRulesChange?.();
    } catch (err: any) {
      setError(`Failed to delete rule: ${err.message}`);
    }
  };

  // Save edited rule
  const handleSaveEdit = async () => {
    if (!editingRule) return;
    try {
      const response = await bomApi.updateRule(editingRule.id, editingRule);
      setRules(rules.map(r => 
        r.id === editingRule.id ? response.rule : r
      ));
      setEditDialogOpen(false);
      setEditingRule(null);
      onRulesChange?.();
    } catch (err: any) {
      setError(`Failed to update rule: ${err.message}`);
    }
  };

  // Move rule to customer folder or make global
  const handleMoveRule = async () => {
    if (!movingRule) return;
    try {
      const updates: Partial<ExtractionRule> = targetFolderId === 'global' 
        ? { scope: 'global' as RuleScope, scope_value: '' }
        : { scope: 'folder' as RuleScope, scope_value: targetFolderId };
      
      const response = await bomApi.updateRule(movingRule.id, updates);
      setRules(rules.map(r => 
        r.id === movingRule.id ? response.rule : r
      ));
      setMoveDialogOpen(false);
      setMovingRule(null);
      setTargetFolderId('');
      onRulesChange?.();
      // Reload folders to update rule counts
      loadFolders();
    } catch (err: any) {
      setError(`Failed to move rule: ${err.message}`);
    }
  };

  // Create new rule
  const handleCreateRule = async () => {
    try {
      setApplyingRule(true);
      const response = await bomApi.createRule({
        name: newRule.name,
        text: patternMode === 'regex' ? (newRule.text || newRule.pattern) : newRule.text,
        pattern: patternMode === 'regex' ? newRule.pattern : undefined,
        component_type: newRule.component_type,
        scope: newRule.scope,
        scope_value: newRule.scope === 'folder' ? newRule.scope_value : undefined,
        category: newRule.category,
        action: newRule.action,
        description: newRule.description,
      });
      setRules([...rules, response.rule]);
      
      // Apply to current extraction if checkbox is checked and we have a job
      if (applyToExtraction && currentJobId && response.rule) {
        try {
          const applyResult = await bomApi.applyRuleIncremental({
            rule_id: response.rule.id,
            job_id: currentJobId,
            pdf_path: currentPdfFilename || undefined,
          });
          if (applyResult.items_added > 0) {
            onRuleApplied?.({ 
              items_added: applyResult.items_added, 
              message: `Rule created and applied! ${applyResult.items_added} new item(s) added.` 
            });
          } else {
            onRuleApplied?.({ 
              items_added: 0, 
              message: 'Rule created. No new matches found - click "Extract All" to re-extract with the new rule.' 
            });
          }
        } catch (applyErr: any) {
          console.error('Failed to apply rule:', applyErr);
          // Rule was created but apply failed - still close dialog
          setError(`Rule created but failed to apply: ${applyErr.message}`);
        }
      }
      
      setCreateDialogOpen(false);
      setPatternMode('example');
      setNewRule(blankRule());
      onRulesChange?.();
      // Reload folders to update rule counts
      loadFolders();
    } catch (err: any) {
      setError(`Failed to create rule: ${err.message}`);
    } finally {
      setApplyingRule(false);
    }
  };

  // Toggle rule expansion
  const toggleExpand = (ruleId: string) => {
    const newExpanded = new Set(expandedRules);
    if (newExpanded.has(ruleId)) {
      newExpanded.delete(ruleId);
    } else {
      newExpanded.add(ruleId);
    }
    setExpandedRules(newExpanded);
  };
  
  // Test rule
  const handleTestRule = async () => {
    if (!testingRule) return;
    setTestLoading(true);
    try {
      const values = testValues.split('\n').map(v => v.trim()).filter(v => v.length > 0);
      const response = await bomApi.testRule(testingRule.id, values.length > 0 ? values : undefined);
      setTestResults(response.results);
    } catch (err: any) {
      setError(`Failed to test rule: ${err.message}`);
    } finally {
      setTestLoading(false);
    }
  };
  
  // Open test dialog
  const openTestDialog = (rule: ExtractionRule) => {
    setTestingRule(rule);
    setTestValues(rule.example || '');
    setTestResults([]);
    setTestDialogOpen(true);
  };

  // Filter rules by search term
  const displayedRules = searchTerm.trim() 
    ? rules.filter(rule => {
        const search = searchTerm.toLowerCase();
        return (
          rule.name?.toLowerCase().includes(search) ||
          rule.pattern?.toLowerCase().includes(search) ||
          rule.component_type?.toLowerCase().includes(search) ||
          rule.description?.toLowerCase().includes(search) ||
          rule.example?.toLowerCase().includes(search)
        );
      })
    : rules;

  return (
    <Paper elevation={compact ? 0 : 2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RuleIcon color="primary" />
            <Typography variant="h6">Extraction Rules</Typography>
            <Chip 
              label={searchTerm ? `${displayedRules.length}/${rules.length}` : rules.length} 
              size="small" 
              color="primary" 
              variant="outlined" 
            />
          </Box>
          <Box>
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={loadRules}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={readOnly ? "Unlock extraction to add rules" : ""}>
              <span>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  variant="contained"
                  onClick={() => {
                    setNewRule(blankRule());
                    setPatternMode('example');
                    setCreateDialogOpen(true);
                  }}
                  disabled={readOnly}
                  sx={{ ml: 1 }}
                >
                  Add Rule
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* Filter and Search */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterIcon fontSize="small" color="action" />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={filterScope}
              onChange={(e) => setFilterScope(e.target.value)}
              displayEmpty
            >
              <MenuItem value="all">All Rules</MenuItem>
              <MenuItem value="global">🌍 Global Only</MenuItem>
              <MenuItem value="folder">📁 All Customer Rules</MenuItem>
              {folders.length > 0 && <Divider />}
              {folders.map(folder => (
                <MenuItem key={folder.id} value={`folder:${folder.id}`}>
                  📁 {folder.customer_name} ({folder.rule_count})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Search */}
          <TextField
            size="small"
            placeholder="Search rules..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, minWidth: 120 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ m: 1 }}>
          {error}
        </Alert>
      )}

      {/* Rules List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : rules.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No extraction rules defined yet.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Select text in the PDF viewer and click "Teach" to create rules.
            </Typography>
          </Box>
        ) : displayedRules.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <SearchIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No rules match "{searchTerm}"
            </Typography>
            <Button size="small" onClick={() => setSearchTerm('')} sx={{ mt: 1 }}>
              Clear Search
            </Button>
          </Box>
        ) : (
          <List dense>
            {displayedRules.map((rule) => (
              <React.Fragment key={rule.id}>
                <ListItem
                  sx={{
                    opacity: rule.enabled ? 1 : 0.5,
                    bgcolor: expandedRules.has(rule.id) ? 'action.hover' : 'transparent',
                  }}
                >
                  <IconButton size="small" onClick={() => toggleExpand(rule.id)}>
                    {expandedRules.has(rule.id) ? <CollapseIcon /> : <ExpandIcon />}
                  </IconButton>
                  
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" fontWeight="bold">
                          {rule.name}
                        </Typography>
                        <Chip
                          size="small"
                          label={rule.scope === 'folder' 
                            ? `📁 ${folders.find(f => f.id === rule.scope_value)?.customer_name || 'Customer'}`
                            : rule.scope
                          }
                          sx={{
                            backgroundColor: SCOPE_COLORS[rule.scope] || SCOPE_COLORS.global,
                            color: 'white',
                            height: 20,
                            fontSize: '0.7rem',
                          }}
                        />
                        <Chip
                          size="small"
                          label={rule.action === 'force_include' ? 'force include' : rule.action}
                          sx={{
                            backgroundColor: ACTION_COLORS[rule.action] || ACTION_COLORS.include,
                            color: 'white',
                            height: 20,
                            fontSize: '0.7rem',
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <PatternIcon fontSize="small" sx={{ opacity: 0.5 }} />
                        <Typography 
                          variant="caption" 
                          component="span"
                          sx={{ fontFamily: 'monospace', opacity: 0.8 }}
                        >
                          {rule.pattern}
                        </Typography>
                        <Typography variant="caption" color="primary">
                          → {rule.component_type}
                        </Typography>
                      </Box>
                    }
                  />
                  
                  <ListItemSecondaryAction>
                    <Tooltip title={readOnly ? "Unlock extraction to modify rules" : ""}>
                      <span>
                        <Switch
                          size="small"
                          checked={rule.enabled}
                          onChange={() => handleToggleRule(rule)}
                          disabled={readOnly}
                        />
                      </span>
                    </Tooltip>
                    <Tooltip title={readOnly ? "Unlock extraction to modify rules" : (rule.scope === 'folder' ? 'Move to Global or Another Customer' : 'Move to Customer Folder')}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={readOnly}
                          onClick={() => {
                            setMovingRule(rule);
                            setTargetFolderId('');
                            setMoveDialogOpen(true);
                          }}
                        >
                          <MoveIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={readOnly ? "Unlock extraction to modify rules" : "Edit rule"}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={readOnly}
                          onClick={() => {
                            setEditingRule({ ...rule });
                            setEditDialogOpen(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={readOnly ? "Unlock extraction to modify rules" : "Delete rule"}>
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={readOnly}
                          onClick={() => {
                            setDeletingRule(rule);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>

                {/* Expanded Details */}
                <Collapse in={expandedRules.has(rule.id)}>
                  <Box sx={{ pl: 6, pr: 2, pb: 2, bgcolor: 'action.hover' }}>
                    {rule.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {rule.description}
                      </Typography>
                    )}
                    {rule.example && (
                      <Typography variant="caption" display="block">
                        <strong>Example:</strong> "{rule.example}"
                      </Typography>
                    )}
                    <Typography variant="caption" display="block" color="text.secondary">
                      Created: {new Date(rule.created_at).toLocaleString()}
                      {rule.created_by && ` by ${rule.created_by}`}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<TestIcon />}
                      onClick={() => openTestDialog(rule)}
                      sx={{ mt: 1 }}
                      variant="outlined"
                    >
                      Test Rule
                    </Button>
                  </Box>
                </Collapse>
                
                <Divider component="li" />
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Rule</DialogTitle>
        <DialogContent>
          {editingRule && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Name"
                fullWidth
                value={editingRule.name}
                onChange={(e) => setEditingRule({ ...editingRule, name: e.target.value })}
              />
              <TextField
                label="Pattern (Regex)"
                fullWidth
                value={editingRule.pattern}
                onChange={(e) => setEditingRule({ ...editingRule, pattern: e.target.value })}
                helperText="Regular expression pattern"
              />
              <FormControl fullWidth>
                <InputLabel>Component Type</InputLabel>
                <Select
                  label="Component Type"
                  value={editingRule.component_type || ''}
                  onChange={(e) => setEditingRule({ ...editingRule, component_type: e.target.value })}
                >
                  {Array.isArray(componentTypes) && componentTypes.map((type) => (
                    <MenuItem key={type.name} value={type.name}>
                      {type.display_name || type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Scope</InputLabel>
                <Select
                  label="Scope"
                  value={editingRule.scope === 'folder' && editingRule.scope_value ? `folder:${editingRule.scope_value}` : editingRule.scope}
                  onChange={(e) => {
                    const value = e.target.value as string;
                    if (value.startsWith('folder:')) {
                      setEditingRule({
                        ...editingRule,
                        scope: 'folder',
                        scope_value: value.replace('folder:', ''),
                      });
                    } else {
                      setEditingRule({
                        ...editingRule,
                        scope: value as RuleScope,
                        scope_value: '',
                      });
                    }
                  }}
                >
                  <MenuItem value="global">🌍 Global (All Customers)</MenuItem>
                  {editingRule.scope === 'project' && <MenuItem value="project">Project</MenuItem>}
                  {editingRule.scope === 'file' && <MenuItem value="file">This file</MenuItem>}
                  {folders.length > 0 && <Divider />}
                  {folders.map(folder => (
                    <MenuItem key={folder.id} value={`folder:${folder.id}`}>
                      📁 {folder.customer_name} Only
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={editingRule.description || ''}
                onChange={(e) => setEditingRule({ ...editingRule, description: e.target.value })}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Rule?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the rule "{deletingRule?.name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteRule} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Move Rule to Customer Dialog */}
      <Dialog open={moveDialogOpen} onClose={() => setMoveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MoveIcon color="primary" />
            Move Rule
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Move "{movingRule?.name}" to a customer folder or make it global.
          </Typography>
          <Typography variant="caption" display="block" sx={{ mb: 1 }}>
            Current scope: <strong>{movingRule?.scope === 'folder' 
              ? `📁 ${folders.find(f => f.id === movingRule?.scope_value)?.customer_name || 'Customer'}`
              : '🌍 Global'
            }</strong>
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Move to</InputLabel>
            <Select
              value={targetFolderId}
              onChange={(e) => setTargetFolderId(e.target.value)}
              label="Move to"
            >
              <MenuItem value="global">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GlobalIcon fontSize="small" color="success" />
                  🌍 Global (applies to all customers)
                </Box>
              </MenuItem>
              {folders.length > 0 && <Divider />}
              {folders.map(folder => (
                <MenuItem 
                  key={folder.id} 
                  value={folder.id}
                  disabled={movingRule?.scope_value === folder.id}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    📁 {folder.customer_name}
                    {movingRule?.scope_value === folder.id && (
                      <Chip label="current" size="small" sx={{ ml: 1 }} />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {targetFolderId && targetFolderId !== 'global' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              This rule will only apply to files in the "{folders.find(f => f.id === targetFolderId)?.customer_name}" folder.
            </Alert>
          )}
          {targetFolderId === 'global' && movingRule?.scope === 'folder' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Making this rule global will apply it to ALL customers.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setMoveDialogOpen(false); setTargetFolderId(''); }}>Cancel</Button>
          <Button 
            onClick={handleMoveRule} 
            color="primary" 
            variant="contained"
            disabled={!targetFolderId || (movingRule?.scope === 'folder' && movingRule?.scope_value === targetFolderId) || (movingRule?.scope === 'global' && targetFolderId === 'global')}
          >
            Move Rule
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Rule Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Rule</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Rule Name"
              fullWidth
              value={newRule.name}
              onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
              placeholder="e.g., Generator Prefix"
            />
            <FormControl fullWidth>
              <InputLabel>Pattern from</InputLabel>
              <Select
                label="Pattern from"
                value={patternMode}
                onChange={(e) => setPatternMode(e.target.value as 'example' | 'regex')}
              >
                <MenuItem value="example">Example text</MenuItem>
                <MenuItem value="regex">Regular expression</MenuItem>
              </Select>
            </FormControl>
            {patternMode === 'regex' ? (
              <TextField
                label="Regular expression"
                fullWidth
                value={newRule.pattern}
                onChange={(e) => setNewRule({ ...newRule, pattern: e.target.value })}
                placeholder="e.g., ^-?G\\d+$"
                helperText="Used as written. Example text is not required."
              />
            ) : (
              <TextField
                label="Example Text"
                fullWidth
                value={newRule.text}
                onChange={(e) => setNewRule({ ...newRule, text: e.target.value })}
                placeholder="e.g., -GENX or FAN100"
                helperText="Enter an example text to generate a pattern"
              />
            )}
            <FormControl fullWidth>
              <InputLabel>Component Type</InputLabel>
              <Select
                label="Component Type"
                value={newRule.component_type || ''}
                onChange={(e) => setNewRule({ ...newRule, component_type: e.target.value })}
              >
                {Array.isArray(componentTypes) && componentTypes.map((type) => (
                  <MenuItem key={type.name} value={type.name}>
                    {type.display_name || type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Scope</InputLabel>
              <Select
                label="Scope"
                value={newRule.scope === 'folder' && newRule.scope_value ? `folder:${newRule.scope_value}` : newRule.scope}
                onChange={(e) => {
                  const value = e.target.value as string;
                  if (value.startsWith('folder:')) {
                    setNewRule({ 
                      ...newRule, 
                      scope: 'folder' as RuleScope, 
                      scope_value: value.replace('folder:', '') 
                    });
                  } else {
                    setNewRule({ 
                      ...newRule, 
                      scope: value as RuleScope, 
                      scope_value: '' 
                    });
                  }
                }}
              >
                <MenuItem value="global">🌍 Global (All Customers)</MenuItem>
                {folders.length > 0 && <Divider />}
                {folders.map(folder => (
                  <MenuItem key={folder.id} value={`folder:${folder.id}`}>
                    📁 {folder.customer_name} Only
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Action</InputLabel>
              <Select
                label="Action"
                value={newRule.action}
                onChange={(e) => setNewRule({ ...newRule, action: e.target.value as RuleAction })}
              >
                <MenuItem value="include">✅ Include (Extract this)</MenuItem>
                <MenuItem value="exclude">❌ Exclude (Ignore this)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Description (Optional)"
              fullWidth
              multiline
              rows={2}
              value={newRule.description}
              onChange={(e) => setNewRule({ ...newRule, description: e.target.value })}
            />
            
            {/* Apply to current extraction option */}
            {currentJobId && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={applyToExtraction}
                    onChange={(e) => setApplyToExtraction(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      Apply to current extraction
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Immediately apply this rule to add matching items (preserves existing verifications)
                    </Typography>
                  </Box>
                }
                sx={{ mt: 1, alignItems: 'flex-start' }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={applyingRule}>Cancel</Button>
          <Button 
            onClick={handleCreateRule} 
            variant="contained"
            disabled={!(patternMode === 'regex' ? newRule.pattern : newRule.text) || !newRule.component_type || applyingRule}
            startIcon={applyingRule ? <CircularProgress size={16} /> : null}
          >
            {applyingRule ? 'Creating & Applying...' : (applyToExtraction && currentJobId ? 'Create & Apply' : 'Create Rule')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Rule Dialog */}
      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Test Rule: {testingRule?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Alert severity="info" sx={{ mb: 1 }}>
              <Typography variant="body2">
                <strong>Pattern:</strong> <code>{testingRule?.pattern}</code>
              </Typography>
              <Typography variant="body2">
                <strong>Action:</strong> {testingRule?.action === 'include' ? '✅ Include' : testingRule?.action === 'force_include' ? '🔥 Force Include' : '❌ Exclude'}
                {testingRule?.component_type && ` → ${testingRule.component_type}`}
              </Typography>
            </Alert>
            
            <TextField
              label="Test Values (one per line)"
              fullWidth
              multiline
              rows={4}
              value={testValues}
              onChange={(e) => setTestValues(e.target.value)}
              placeholder={`Enter values to test, e.g.:\n-GENX\nGEN1\nQ1\nFAN100`}
              helperText="Enter component references to test against the rule pattern"
            />
            
            <Button 
              variant="contained" 
              onClick={handleTestRule}
              disabled={testLoading}
              startIcon={testLoading ? <CircularProgress size={16} /> : <TestIcon />}
            >
              {testLoading ? 'Testing...' : 'Run Test'}
            </Button>
            
            {/* Test Results */}
            {testResults.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Results ({testResults.filter(r => r.matched).length}/{testResults.length} matched):
                </Typography>
                {onHighlightReference && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    💡 Click on a matched result to highlight it in the PDF
                  </Typography>
                )}
                <List dense>
                  {testResults.map((result, idx) => (
                    <ListItem 
                      key={idx} 
                      sx={{ 
                        py: 0.5,
                        cursor: result.matched && onHighlightReference ? 'pointer' : 'default',
                        '&:hover': result.matched && onHighlightReference ? {
                          bgcolor: 'action.hover',
                          borderRadius: 1,
                        } : {},
                      }}
                      onClick={() => {
                        if (result.matched && onHighlightReference) {
                          onHighlightReference(result.value, result.component_type);
                        }
                      }}
                    >
                      {result.matched ? (
                        <MatchIcon color="success" sx={{ mr: 1 }} />
                      ) : (
                        <NoMatchIcon color="error" sx={{ mr: 1 }} />
                      )}
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontFamily: 'monospace',
                                color: result.matched ? 'success.main' : 'error.main'
                              }}
                            >
                              {result.value}
                            </Typography>
                            {result.matched && result.component_type && (
                              <Chip 
                                size="small" 
                                label={`→ ${result.component_type}`}
                                color="primary"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                            {result.matched && onHighlightReference && (
                              <Tooltip title="Click to highlight in PDF">
                                <Chip 
                                  size="small" 
                                  label="View"
                                  variant="outlined"
                                  color="info"
                                  sx={{ height: 20, fontSize: '0.65rem' }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        }
                        secondary={
                          result.matched 
                            ? `Will be ${result.action === 'include' || result.action === 'force_include' ? 'extracted' : 'excluded'}${result.action === 'force_include' ? ' (force override)' : ''}` 
                            : 'No match - rule will not apply'
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default RulesPanel;

